<template>
  <article class="wagb-home">
    <HomeHero :hero="hero" />

    <div class="wagb-home__intro">
      <p v-for="(para, idx) in homeIntro" :key="idx" v-html="para" />
    </div>

    <nav
      class="wagb-home__toc"
      :class="{ 'is-open': tocOpen }"
      aria-label="Table of Contents"
    >
      <button
        type="button"
        class="wagb-home__toc-toggle"
        :aria-expanded="tocOpen"
        @click="tocOpen = !tocOpen"
      >
        <span class="wagb-home__toc-heading">
          <span class="wagb-home__toc-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                fill="currentColor"
                d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z"
              />
            </svg>
          </span>
          <span class="wagb-home__toc-title-wrap">
            <span class="wagb-home__toc-title">{{ homeCopy.tocTitle }}</span>
            <span class="wagb-home__toc-meta">{{ tocSections.length }} {{ homeCopy.tocMetaSuffix }}</span>
          </span>
        </span>
        <span class="wagb-home__toc-chevron" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>

      <div class="wagb-home__toc-panel">
        <ol class="wagb-home__toc-list">
          <li v-for="(item, idx) in tocSections" :key="item.id">
            <a :href="'#' + item.id" class="wagb-home__toc-link">
              <span class="wagb-home__toc-num">{{ idx + 1 }}</span>
              <span class="wagb-home__toc-text">{{ item.title }}</span>
            </a>
          </li>
        </ol>
      </div>
    </nav>

    <WhatIsSection :section="whatIsSection" />

    <section :id="tocSections[1].id">
      <h2>{{ tocSections[1].title }}</h2>
      <div class="wagb-home__features">
        <div v-for="feat in features" :key="feat.title" class="wagb-home__feature-card">
          <h3>{{ feat.title }}</h3>
          <p class="feature-sub">{{ feat.subtitle }}</p>
          <ul>
            <li v-for="item in feat.items" :key="item">{{ item }}</li>
          </ul>
        </div>
      </div>
    </section>

    <InstallGuideSection
      :section-id="tocSections[2].id"
      :title="tocSections[2].title"
      :steps="installSteps"
      :section="installSection"
    />

    <section :id="tocSections[3].id">
      <h2>{{ tocSections[3].title }}</h2>
      <p>{{ homeCopy.pc.intro }}</p>
      <figure class="wagb-home__figure">
        <img :src="images.pcHero" :alt="homeCopy.pc.imgAlt" width="800" height="450" loading="lazy" />
      </figure>
      <h3>{{ homeCopy.pc.methodsHeading }}</h3>
      <div class="wagb-home__pc-methods">
        <div v-for="method in homeCopy.pc.methods" :key="method.title" class="wagb-home__pc-card">
          <h3>{{ method.title }}</h3>
          <p>{{ method.desc }}</p>
        </div>
      </div>
      <router-link :to="homeCopy.pc.ctaLink" class="wagb-home__cta wagb-home__cta--inline">
        {{ homeCopy.pc.cta }}
      </router-link>
    </section>

    <section :id="tocSections[4].id">
      <h2>{{ tocSections[4].title }}</h2>
      <p v-html="homeCopy.update.intro" />
      <h3>{{ homeCopy.update.stepsHeading }}</h3>
      <ul>
        <li v-for="step in homeCopy.update.steps" :key="step">{{ step }}</li>
      </ul>
      <h3>{{ homeCopy.update.noteHeading }}</h3>
      <p>{{ homeCopy.update.noteText }}</p>
    </section>

    <section :id="tocSections[5].id">
      <h2>{{ tocSections[5].title }}{{ homeCopy.version.titleSuffix }}</h2>
      <p>{{ homeCopy.version.intro }}</p>
      <h3>{{ homeCopy.version.whatsNewHeading }}</h3>
      <ul>
        <li v-for="item in homeCopy.version.whatsNew" :key="item">{{ item }}</li>
      </ul>
      <h3>{{ homeCopy.version.feedbackHeading }}</h3>
      <div class="wagb-home__quotes">
        <blockquote v-for="fb in userFeedback" :key="fb.author" class="wagb-home__quote">
          <p>"{{ fb.text }}"</p>
          <cite>— {{ fb.author }}</cite>
        </blockquote>
      </div>
      <router-link :to="homeCopy.version.ctaLink" class="wagb-home__cta wagb-home__cta--inline">
        {{ homeCopy.version.cta }}
      </router-link>
    </section>

    <section :id="tocSections[6].id">
      <h2>{{ tocSections[6].title }}</h2>
      <p>{{ homeCopy.comparison.intro }}</p>
      <h3>{{ homeCopy.comparison.heading }}</h3>
      <table class="wagb-home__compare-table">
        <thead>
          <tr>
            <th>{{ homeCopy.comparison.tableHeaders.feature }}</th>
            <th>{{ homeCopy.comparison.tableHeaders.gb }}</th>
            <th>{{ homeCopy.comparison.tableHeaders.official }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in comparisonRows" :key="row.feature">
            <td>{{ row.feature }}</td>
            <td>{{ row.gb }}</td>
            <td>{{ row.official }}</td>
          </tr>
        </tbody>
      </table>
      <div class="wagb-home__notice">
        <span class="wagb-home__notice-icon">⚠️</span>
        <p>{{ homeCopy.comparison.notice }}</p>
      </div>
    </section>

    <section :id="tocSections[7].id">
      <h2>{{ tocSections[7].title }}</h2>
      <p v-for="(para, idx) in homeCopy.banFix.intro" :key="idx">{{ para }}</p>
      <h3>{{ homeCopy.banFix.stepsHeading }}</h3>
      <ol class="wagb-home__numbered-list">
        <li v-for="step in banFixSteps" :key="step.title">
          <strong>{{ step.title }}</strong>
          {{ step.desc }}
        </li>
      </ol>
    </section>

    <section :id="tocSections[8].id">
      <h2>{{ tocSections[8].title }}</h2>
      <p>{{ homeCopy.avoidBan.intro }}</p>
      <h3>{{ homeCopy.avoidBan.tipsHeading }}</h3>
      <ul class="wagb-home__tip-list">
        <li v-for="tip in avoidBanTips" :key="tip.title">
          <strong>{{ tip.title }}</strong>
          {{ tip.desc }}
        </li>
      </ul>
    </section>

    <section :id="tocSections[9].id">
      <h2>{{ tocSections[9].title }}</h2>
      <div class="wagb-home__problems">
        <div v-for="problem in commonProblems" :key="problem.title" class="wagb-home__problem-card">
          <div class="wagb-home__problem-card-header">
            <span>{{ problem.icon }}</span>
            <h3>{{ problem.title }}</h3>
          </div>
          <p class="problem-desc">{{ problem.desc }}</p>
          <p class="problem-solution">
            <strong>{{ homeCopy.problems.solutionLabel }}</strong> {{ problem.solution }}
          </p>
        </div>
      </div>
      <router-link :to="homeCopy.problems.ctaLink" class="wagb-home__cta wagb-home__cta--inline">
        {{ homeCopy.problems.cta }}
      </router-link>
    </section>

    <section :id="tocSections[10].id">
      <h2>{{ tocSections[10].title }}</h2>
      <p>{{ homeCopy.oldVersions.intro }}</p>
      <h3>{{ homeCopy.oldVersions.heading }}</h3>
      <table class="wagb-home__version-table">
        <thead>
          <tr>
            <th>{{ homeCopy.oldVersions.tableHeaders.version }}</th>
            <th>{{ homeCopy.oldVersions.tableHeaders.date }}</th>
            <th>{{ homeCopy.oldVersions.tableHeaders.baseVersion }}</th>
            <th>{{ homeCopy.oldVersions.tableHeaders.size }}</th>
            <th>{{ homeCopy.oldVersions.tableHeaders.android }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ver in oldVersions" :key="ver.version">
            <td>{{ ver.version }}</td>
            <td>{{ ver.date }}</td>
            <td>{{ ver.baseVersion }}</td>
            <td>{{ ver.size }}</td>
            <td>{{ ver.android }}</td>
          </tr>
        </tbody>
      </table>
      <router-link :to="homeCopy.oldVersions.ctaLink" class="wagb-home__cta wagb-home__cta--inline">
        {{ homeCopy.oldVersions.cta }}
      </router-link>
    </section>

    <section :id="tocSections[11].id">
      <h2>{{ tocSections[11].title }}</h2>
      <div class="wagb-home__faq">
        <div
          v-for="(item, idx) in faqItems"
          :key="item.q"
          class="wagb-home__faq-item"
          :class="{ 'is-open': openFaq === idx }"
        >
          <button
            type="button"
            class="wagb-home__faq-question"
            :aria-expanded="openFaq === idx"
            @click="toggleFaq(idx)"
          >
            ❓ {{ item.q }}
          </button>
          <div v-show="openFaq === idx" class="wagb-home__faq-answer">
            <p>{{ item.a }}</p>
          </div>
        </div>
      </div>
    </section>
  </article>
</template>

<script>
import HomeHero from './HomeHero.vue';
import WhatIsSection from './WhatIsSection.vue';
import InstallGuideSection from './InstallGuideSection.vue';
import { getHomeContent } from '@/data/wagbappro/home-content';

export default {
  name: 'HomeContent',
  components: { HomeHero, WhatIsSection, InstallGuideSection },
  props: {
    locale: {
      type: String,
      default: 'id',
    },
  },
  data() {
    return {
      openFaq: 0,
      tocOpen: true,
    };
  },
  computed: {
    content() {
      return getHomeContent(this.locale);
    },
    hero() {
      return this.content.hero;
    },
    homeIntro() {
      return this.content.homeIntro;
    },
    homeCopy() {
      return this.content.homeCopy;
    },
    tocSections() {
      return this.content.tocSections;
    },
    whatIsSection() {
      return this.content.whatIsSection;
    },
    features() {
      return this.content.features;
    },
    installSteps() {
      return this.content.installSteps;
    },
    installSection() {
      return this.content.installSection;
    },
    comparisonRows() {
      return this.content.comparisonRows;
    },
    banFixSteps() {
      return this.content.banFixSteps;
    },
    avoidBanTips() {
      return this.content.avoidBanTips;
    },
    commonProblems() {
      return this.content.commonProblems;
    },
    oldVersions() {
      return this.content.oldVersions;
    },
    faqItems() {
      return this.content.faqItems;
    },
    userFeedback() {
      return this.content.userFeedback;
    },
    images() {
      return this.content.images;
    },
  },
  methods: {
    toggleFaq(idx) {
      this.openFaq = this.openFaq === idx ? -1 : idx;
    },
  },
};
</script>
